import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createColumnHelper, type PaginationState, type SortingState } from '@tanstack/react-table';
import { CheckCircle2, Circle, MoreVertical, Plus, Search, X } from 'lucide-react';
import { useBlockLender, useLendersList, useUnblockLender } from '../../hooks/useLenders';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../components/common/TablePagination';
import LenderStatusDialog, { type LenderStatusAction } from './LenderStatusDialog';
import type { LenderLos, LendersListItem, LendersListSort } from '../../api/lenders';

const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;
const SEARCH_DELAY_MS = 300;

// The wizard's names for them, which the story uses too.
const LOS_LABELS: Record<LenderLos, string> = {
  encompass: 'Encompass',
  meridianlink: 'MeridianLink Consumer',
  'meridianlink-mortgage': 'MeridianLink Mortgage',
};

function formatCreated(value: string | null): string {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

type PendingStatusChange = { lender: LendersListItem; action: LenderStatusAction };

const menuItemClassName = 'block w-full px-3 py-2 text-left text-sm text-content hover:bg-surface-sunken';

function RowActions({
  lender,
  onEdit,
  onStatusChange,
}: {
  lender: LendersListItem;
  onEdit: (lender: LendersListItem) => void;
  onStatusChange: (change: PendingStatusChange) => void;
}) {
  const [open, setOpen] = useState(false);
  const isActive = lender.status !== 'blocked';

  return (
    <div
      className="relative flex justify-end"
      // Closes when the focus leaves the menu, which covers both clicking elsewhere and tabbing away.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${lender.displayName}`}
        className="btn-icon"
        // The row navigates on click; the menu and its items are not the row.
        onClick={(event) => {
          event.stopPropagation();
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-8 z-10 w-44 rounded-md border border-border bg-surface py-1 shadow-lg">
          <button
            type="button"
            role="menuitem"
            className={menuItemClassName}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onEdit(lender);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className={menuItemClassName}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onStatusChange({ lender, action: isActive ? 'deactivate' : 'activate' });
            }}
          >
            {isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      )}
    </div>
  );
}

const columnHelper = createColumnHelper<LendersListItem>();

// Each column's id is the key the server sorts by.
const dataColumns = [
  columnHelper.accessor('displayName', {
    id: 'lender',
    header: 'Lender',
    cell: (info) => <span className="text-content">{info.getValue()}</span>,
  }),
  columnHelper.accessor('entityId', {
    id: 'entityId',
    header: 'Entity ID',
    cell: (info) => <span className="text-content-secondary">{info.getValue() ?? '—'}</span>,
  }),
  columnHelper.accessor('los', {
    id: 'los',
    header: 'LOS',
    cell: (info) => {
      const labels = info
        .getValue()
        .map((los) => LOS_LABELS[los])
        .join(', ');
      return (
        <span className="block max-w-[220px] truncate text-content-secondary" title={labels || undefined}>
          {labels || '—'}
        </span>
      );
    },
  }),
  columnHelper.accessor('userCount', {
    id: 'users',
    header: 'Users',
    cell: (info) => <span className="text-sm text-content-secondary">{info.getValue()}</span>,
  }),
  columnHelper.accessor('productCount', {
    id: 'products',
    header: 'Products',
    cell: (info) => <span className="text-sm text-content-secondary">{info.getValue()}</span>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: (info) => (
      <span className="inline-flex items-center gap-1.5 text-content-secondary">
        {info.getValue() === 'blocked' ? (
          <>
            <Circle className="h-3.5 w-3.5 text-content-muted" aria-hidden="true" />
            Inactive
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 fill-brand text-white" aria-hidden="true" />
            Active
          </>
        )}
      </span>
    ),
  }),
  columnHelper.accessor('creationDate', {
    id: 'created',
    header: 'Created',
    cell: (info) => <span className="text-content-secondary">{formatCreated(info.getValue())}</span>,
  }),
];

export default function LendersListPage() {
  const navigate = useNavigate();
  // A one-time confirmation handed over by the onboarding wizard (OM-3921 AC12). Read once into
  // state so a refresh or a later visit does not show it again.
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(
    () => (location.state as { notice?: string } | null)?.notice ?? null,
  );

  const blockLender = useBlockLender();
  const unblockLender = useUnblockLender();
  const [statusChange, setStatusChange] = useState<PendingStatusChange | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const isStatusPending = blockLender.isPending || unblockLender.isPending;

  // Newest first: the lender the wizard just created lands at the top, next to its notice.
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  // The server is asked once the typing stops, and a new search starts over from the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = search.trim();
      if (next === term) return;
      setTerm(next);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, term]);

  const { data, isLoading } = useLendersList({
    offset: pagination.pageIndex * PAGE_SIZE,
    limit: PAGE_SIZE,
    sort: sorting[0].id as LendersListSort,
    order: sorting[0].desc ? 'desc' : 'asc',
    search: term || undefined,
  });

  const handleSortingChange = (next: SortingState) => {
    setSorting(next);
    // A new order starts over from the first page.
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const openLender = (lender: LendersListItem) => navigate(`/lenders/${encodeURIComponent(lender.urn)}`);

  const confirmStatusChange = async () => {
    if (!statusChange) return;

    setStatusError(null);
    try {
      const change = statusChange.action === 'deactivate' ? blockLender : unblockLender;
      await change.mutateAsync(statusChange.lender.urn);
      setStatusChange(null);
    } catch {
      setStatusError(
        statusChange.action === 'deactivate'
          ? 'Unable to deactivate this lender. Please try again.'
          : 'Unable to activate this lender. Please try again.',
      );
    }
  };

  const columns = useMemo(
    () => [
      ...dataColumns,
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <RowActions
            lender={info.row.original}
            onEdit={openLender}
            onStatusChange={(change) => {
              setStatusError(null);
              setStatusChange(change);
            }}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div>
      <PageHeader
        title="Lenders"
        actions={
          <button onClick={() => navigate('/lenders/new')} className="btn-primary gap-1">
            <Plus className="h-4 w-4" />
            New Lender
          </button>
        }
      />
      {notice && (
        <div
          role="status"
          className="mb-4 flex items-center justify-between rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200"
        >
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {notice}
          </span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="btn-icon">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="relative mb-3 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content" />
        <input
          type="text"
          aria-label="Search lenders"
          placeholder="Search ..."
          className="input pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <DataTable
        data={data?.items ?? []}
        columns={columns}
        totalRows={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        isLoading={isLoading}
        onRowClick={openLender}
      />
      {statusChange && (
        <LenderStatusDialog
          action={statusChange.action}
          lenderName={statusChange.lender.displayName}
          isPending={isStatusPending}
          errorMessage={statusError}
          onConfirm={confirmStatusChange}
          onCancel={() => {
            setStatusChange(null);
            setStatusError(null);
          }}
        />
      )}
    </div>
  );
}
